import { useCallback, useSyncExternalStore } from 'react';
import type { AssemblyView } from './assemblyScene';

// THE SCENE ON SCREEN, so the controls beside it can ask it a question.
//
// Everything the panel tells the viewer goes DOWN through a store — the marks it draws, which
// hand is up, which faces are hidden — because the two are siblings with a modal between them
// and a prop would thread the viewer's state through half the phase. This is the same road in
// the other direction: a question only the loaded mesh can answer.
//
// WHICH THERE IS EXACTLY ONE OF. `clash` already argued why a measurement of the meshes is
// asked of the scene rather than computed beside it — the geometry is there and nowhere else —
// and the same holds for how much wood a bore crosses. See AssemblyView.thickness.
//
// LAST ONE UP WINS. This part is drawn in two viewers at once, the panel's and the modal's,
// and both mount the same mesh: whichever landed most recently answers, and the answer is the
// same either way. Cleared on unmount only by the scene that set it, so a modal closing does
// not take the panel's away.
//
// A STAMP BESIDE IT, because a caller has to know the answer may have changed. A new scene is
// a new mesh — drilled, filled, rescaled — and a thickness measured off the old one is a
// reading of something that is not there any more.
let live: AssemblyView | null = null;
let stamp = 0;
const listeners = new Set<() => void>();
const bump = () => { stamp += 1; listeners.forEach((l) => l()); };

export const setPartScene = (view: AssemblyView | null): void => {
  live = view;
  bump();
};

export const clearPartScene = (view: AssemblyView): void => {
  if (live === view) setPartScene(null);
};

export const usePartScene = (): { view: AssemblyView | null; stamp: number } => {
  const subscribe = useCallback((cb: () => void) => {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);
  // The snapshot has to be a stable reference between bumps or useSyncExternalStore loops, so
  // it is rebuilt only when the stamp moves.
  const snap = useCallback(() => {
    if (held.stamp !== stamp || held.view !== live) held = { view: live, stamp };
    return held;
  }, []);
  return useSyncExternalStore(subscribe, snap);
};

let held: { view: AssemblyView | null; stamp: number } = { view: null, stamp: 0 };
